import type { CSSProperties } from "react";

/**
 * Colour-bearing declarations, dropped from a caller-supplied style when the
 * element renders inside a table cell (TR-3, docs/audit/TABLE-RULES.md).
 *
 * WHY THIS IS SHARED RATHER THAN INLINE IN EACH COMPONENT. Two components need
 * it — `badge.tsx` strips the Salt Tag capsule and `status-badge.tsx` strips the
 * StatusIndicator — and both had the same hole in the same shape: the structural
 * fix removed the CHIP while the caller's own `style`/`textStyle` kept the
 * COLOUR. Two copies of this list would drift, and the whole point of the
 * in-cell context is that the rule lives in one place rather than at every call
 * site.
 *
 * TYPOGRAPHY IS DELIBERATELY NOT IN THIS SET. TR-3 removes the swatch, not the
 * type scale: `fontSize`, `fontWeight`, `fontStyle` and `letterSpacing` all pass
 * through. Over-stripping would break real callers that only want to adjust
 * emphasis.
 */
const COLOUR_DECLARATIONS = new Set([
  "color",
  "background",
  "backgroundColor",
  "backgroundImage",
  "border",
  "borderColor",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "outlineColor",
  "boxShadow",
  "fill",
  "stroke",
]);

/**
 * Returns `style` without any colour-bearing declaration. Custom properties are
 * dropped only when their NAME implies colour — Salt's Tag tinting variables
 * (`--tag-secondary-background` and friends) have nothing left to tint once the
 * Tag is gone, and they are how the destructive variant paints.
 */
export function withoutColour(style?: CSSProperties): CSSProperties | undefined {
  if (!style) return style;
  const kept: Record<string, unknown> = {};
  for (const [property, value] of Object.entries(style)) {
    if (COLOUR_DECLARATIONS.has(property)) continue;
    if (property.startsWith("--") && /(background|color)/i.test(property)) continue;
    kept[property] = value;
  }
  return kept as CSSProperties;
}
